import { useNavigate, useParams } from 'react-router-dom';
import { useAddOnGroupQuery } from '../hooks/useAddOnGroupQuery';
import { useAddOnGroupMutations } from '../hooks/useAddOnGroupMutations';
import { AddOnGroupForm } from '../components/AddOnGroupForm';
import type { AddOnGroupFormValues } from '../schemas/addonGroup.schema';
import { Alert, TextLink } from '@/shared/components';

export function AddOnGroupEdit() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const query = useAddOnGroupQuery(id);
    const { updateMutation } = useAddOnGroupMutations();

    if (!id) return <p className="text-mp-error">Invalid group ID.</p>;
    if (query.isLoading) return <p>Loading group...</p>;
    if (query.isError || !query.data) {
        return <Alert variant="error"><span>Group not found or failed to load.</span></Alert>;
    }

    const handleSubmit = (data: AddOnGroupFormValues) => {
        updateMutation.mutate(
            { id, payload: { name: data.name, description: data.description, addon_ids: data.addon_ids } },
            { onSuccess: () => navigate(`/smr-admin/addon-groups/${id}`) },
        );
    };

    return (
        <section className="space-y-4">
            <div className="flex items-center gap-2">
                <TextLink to={`/smr-admin/addon-groups/${id}`} color="secondary" className="text-sm">&larr; Back to Group</TextLink>
            </div>
            <h1 className="text-2xl font-semibold">Edit: {query.data.name}</h1>
            {updateMutation.isError && (
                <Alert variant="error">
                    <span>{(updateMutation.error as Error)?.message ?? 'Failed to update group.'}</span>
                </Alert>
            )}
            <AddOnGroupForm
                initialValues={query.data}
                onSubmit={handleSubmit}
                isLoading={updateMutation.isPending}
                submitLabel="Save Changes"
            />
        </section>
    );
}
